import * as PointsOfSaleTypes from './pointsOfSale.types';
import * as api from 'api/endpoints/pointsOfSale/pointsOfSale.api';
import { pointsOfSaleError } from 'utils/errorCodes';
import { notificationErr } from 'views/components/notification';
import { messageError, messageSuccess, messageWarning } from 'views/components/message';
import { IcreatePointOfSaleActionProps } from 'interfaces/pointsOfSale.interface';
import {
  IcreatePointOfSaleAPIBody,
  IPointOfSaleState,
  IupdatePointOfSaleAPIBody,
  IupdatePointOfSaleAPIBodyUpdate,
} from 'api/endpoints/pointsOfSale/pointsOfSale.response.interface';
import { ITranslate } from 'interfaces/general.interface';
import { ICountryAPIResponseItem } from 'api/endpoints/general/general.response.interface';
import { TpointsOfSaleDispatchReducer } from './pointsOfSale.reducer';

export async function getPointsOfSaleAction({
  dispatch,
  clientId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TpointsOfSaleDispatchReducer>;
  clientId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: PointsOfSaleTypes.IS_LOADING_GET_POINTS_OF_SALE, payload: true });
  try {
    const [pointsOfSaleFetch] = await Promise.all([
      api.getPointsOfSaleAPI({ clientId, token }),
    ]);

    if (pointsOfSaleFetch.response.status === 200) {
      const sortPointsOfSale = pointsOfSaleFetch.data.point_of_sales.sort((a, b) =>
        a.createdAt < b.createdAt ? -1 : 1
      );
      let ps: IPointOfSaleState[] = [];
      for (const pointOfSale of sortPointsOfSale) {
        pointOfSale.categories = pointOfSale.point_of_sale_categories?.map(
          (category) => category.point_of_sale_category.id
        );
        ps.push(pointOfSale);
      }
      dispatch({
        type: PointsOfSaleTypes.GET_POINTS_OF_SALE,
        payload: ps,
      });
      return true;
    }
    messageError(translate('action_points-of-sale_get-points-of-sale_error'));
    notificationErr(pointsOfSaleError.get_points_of_sale_002, translate);
    return false;
  } catch (err) {
    notificationErr(pointsOfSaleError.get_points_of_sale_001, translate);
    return false;
  } finally {
    dispatch({ type: PointsOfSaleTypes.IS_LOADING_GET_POINTS_OF_SALE, payload: false });
  }
}

export function selectPointOfSaleAction({
  dispatch,
  pointOfSale,
}: {
  dispatch: React.Dispatch<TpointsOfSaleDispatchReducer>;
  pointOfSale: IPointOfSaleState | null;
}) {
  return dispatch({
    type: PointsOfSaleTypes.SELECT_POINT_OF_SALE,
    payload: pointOfSale,
  });
}

export async function createPointOfSaleAction({
  dispatch,
  pointOfSale,
  countries,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TpointsOfSaleDispatchReducer>;
  pointOfSale: IcreatePointOfSaleActionProps;
  countries: Array<ICountryAPIResponseItem>;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const brandsToAdd = pointOfSale.point_of_sale_brand_works;
    delete pointOfSale.point_of_sale_brand_works;

    const body: IcreatePointOfSaleAPIBody = {
      brand_works:
        brandsToAdd && brandsToAdd?.length > 0 ? brandsToAdd?.map((item) => item.id) : [],
      point_of_sale: pointOfSale,
    };

    const createFetch = await api.createPointsOfSaleAPI({ body, token });

    if (createFetch.response.status === 201 || createFetch.response.status === 200) {
      const assignCategories = await api.assignCategoriesAPI({
        pointOfSaleId: createFetch.data.point_of_sale.id,
        categories: pointOfSale.categories as string[],
        token,
      });
      let newPointOfSale: IPointOfSaleState = {
        ...createFetch.data.point_of_sale,
        point_of_sale_brand_works:
          brandsToAdd && brandsToAdd.length > 0
            ? brandsToAdd?.map((item) => {
                return {
                  brand_id: item.id,
                  brand: {
                    name: item.name,
                    logo: item.logo,
                  },
                };
              })
            : [],
        country: {
          id: createFetch.data.point_of_sale.country_id as string,
          name:
            countries.find(
              (country) => country.id === createFetch.data.point_of_sale.country_id
            )?.name ?? '',
        },
      };
      if (
        assignCategories.response.status === 200 ||
        assignCategories.response.status === 201
      ) {
        newPointOfSale.categories = pointOfSale.categories;
      } else {
        messageError(
          translate('action_points-of-sale_assign-point-of-sale-categories_error')
        );
        notificationErr(pointsOfSaleError.create_point_of_sale_003, translate);
      }
      messageSuccess(translate('action_points-of-sale_create-point-of-sale_success'));
      dispatch({
        type: PointsOfSaleTypes.CREATE_POINT_OF_SALE,
        payload: newPointOfSale,
      });
      return true;
    }
    messageError(translate('action_points-of-sale_create-point-of-sale_error'));
    notificationErr(pointsOfSaleError.create_point_of_sale_002, translate);
    return false;
  } catch (err) {
    messageError(translate('action_points-of-sale_create-point-of-sale_error'));
    notificationErr(pointsOfSaleError.create_point_of_sale_001, translate);
    return false;
  }
}

export async function updatePointOfSaleAction({
  dispatch,
  pointOfSale,
  countries,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TpointsOfSaleDispatchReducer>;
  pointOfSale: IupdatePointOfSaleAPIBodyUpdate;
  countries: Array<ICountryAPIResponseItem>;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const brandsToAdd = pointOfSale.point_of_sale_brand_works;
    if (pointOfSale.online === true) {
      pointOfSale.address = '';
      pointOfSale.province = '';
      pointOfSale.postal_code = '';
      pointOfSale.lat = 0;
      pointOfSale.lng = 0;
      pointOfSale.floor = '';
      pointOfSale.door = '';
      delete pointOfSale.country_id;
      pointOfSale.city = '';
      pointOfSale.area = null;
    }
    const body: IupdatePointOfSaleAPIBody = {
      filter: { id: pointOfSale.id },
      update: pointOfSale,
      brand_works_id:
        brandsToAdd && brandsToAdd.length > 0 ? brandsToAdd.map((item) => item.id) : [],
    };

    delete pointOfSale.point_of_sale_brand_works;
    const updateFetch = await api.updatePointOfSaleAPI({ body, token });
    if (updateFetch.response.status === 403) {
      messageWarning(translate('not-authorized-warning'));
      return false;
    }
    if (updateFetch.response.status === 200) {
      const assignCategories = await api.assignCategoriesAPI({
        pointOfSaleId: pointOfSale.id,
        categories: pointOfSale.categories as string[],
        token,
      });

      let newPointOfSale: Partial<IPointOfSaleState> = {
        ...pointOfSale,
        point_of_sale_brand_works:
          brandsToAdd && brandsToAdd.length > 0
            ? brandsToAdd.map((item: any) => {
                return {
                  brand_id: item.id,
                  brand: {
                    name: item.name,
                    logo: item.logo,
                  },
                };
              })
            : [],
        country: {
          id: pointOfSale.country_id as string,
          name: countries.find((country) => country.id === pointOfSale.country_id)
            ?.name as string,
        },
      };
      if (
        assignCategories.response.status === 200 ||
        assignCategories.response.status === 201
      ) {
        newPointOfSale.categories = pointOfSale.categories;
      } else {
        messageError(
          translate('action_points-of-sale_assign-point-of-sale-categories_error')
        );
        notificationErr(pointsOfSaleError.update_point_of_sale_003, translate);
      }
      messageSuccess(translate('action_points-of-sale_update-point-of-sale_success'));
      dispatch({
        type: PointsOfSaleTypes.UPDATE_POINT_OF_SALE,
        payload: {
          pointOfSaleId: pointOfSale.id,
          pointOfSale: newPointOfSale,
        },
      });
      return true;
    }
    messageError(translate('action_points-of-sale_update-point-of-sale_error'));
    notificationErr(pointsOfSaleError.update_point_of_sale_002, translate);
    return false;
  } catch (err) {
    messageError(translate('action_points-of-sale_update-point-of-sale_error'));
    notificationErr(pointsOfSaleError.update_point_of_sale_001, translate);
    return false;
  }
}

export async function removePointOfSaleAction({
  dispatch,
  pointOfSaleId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TpointsOfSaleDispatchReducer>;
  pointOfSaleId: string;
  token: string;
  translate: ITranslate;
}) {
  try {
    const removeFetch = await api.removePointOfSaleAPI({ pointOfSaleId, token });
    if (removeFetch.response.status === 403) {
      messageWarning(
        translate('action_points-of-sale_delete-point-of-sale_not-authorized-error')
      );
      return false;
    }
    if (removeFetch.response.status === 200 && removeFetch.data.point_of_sale > 0) {
      messageSuccess(translate('action_points-of-sale_delete-point-of-sale_success'));
      dispatch({
        type: PointsOfSaleTypes.REMOVE_POINT_OF_SALE,
        payload: pointOfSaleId,
      });
      return true;
    } else {
      messageError(translate('action_points-of-sale_delete-point-of-sale_error'));
      notificationErr(pointsOfSaleError.remove_point_of_sale_002, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('action_points-of-sale_delete-point-of-sale_error'));
    notificationErr(pointsOfSaleError.remove_point_of_sale_001, translate);
    return false;
  }
}
